import * as React from "react";
import {FormattedPageTitle} from "@/components/pages";
import DeleteFaxForm from "@/components/fax/delete/DeleteFaxForm";
import {FormActionButtons, FormattedFormRow, FormContainer} from "@dvrt/graphql-widgets";
import {Typography} from "@mui/material";
import {defineMessage, FormattedMessage} from "react-intl";
import Section from "@/components/Section";
import {useQuery} from "@apollo/client";
import Loading from "@/components/breadcrumbs/Loading";
import TargetSelector, {Target, TargetType} from "@/components/TargetSelector";
import {Link} from "react-router";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ReferenceType} from "@/components/fax/delete/DeleteFaxForm";
import {
    FaxRelatedDataDocument,
    FaxRelatedDataQuery,
    FaxRelatedDataQueryVariables,
    GetTargetsQuery,
    GetTargetsQueryVariables,
    GetTargetsDocument,
} from "@/components/fax/queries.generated";
import {msg_delete} from "@/utils/translations";

const DeleteFaxPage = ({domainId, faxId}: {domainId: number; faxId: number}) => {
    const {loading, data} = useQuery<FaxRelatedDataQuery, FaxRelatedDataQueryVariables>(FaxRelatedDataDocument, {
        variables: {faxId},
    });

    const targetsData = useQuery<GetTargetsQuery, GetTargetsQueryVariables>(GetTargetsDocument, {
        variables: {domainId},
    });

    const form = new DeleteFaxForm(domainId, faxId);

    const exclamationIcon = () => <FontAwesomeIcon style={{color: "red", marginRight: "10px"}} icon="exclamation" />;

    if (loading || targetsData.loading) return <Loading />;

    function getTarget(referenceType: string): Target[] {
        if (referenceType === ReferenceType.USER) {
            const extensionTargets: Target[] = targetsData.data.gateways.items.map((gw) => ({
                numberId: gw.number.id,
                displayName: gw.displayName,
                shortNumber: gw.number.shortNumber,
                type: TargetType.GW,
            }));

            return extensionTargets.concat(
                targetsData.data.faxes.items
                    .filter((fax) => fax.id !== faxId)
                    .map(
                        (fax): Target => ({
                            numberId: fax.number.id,
                            displayName: null,
                            shortNumber: fax.number.shortNumber,
                            type: TargetType.FAX,
                        }),
                    ),
            );
        } else {
            const ivrTargets = targetsData.data.faxes.items
                .filter((fax) => fax.id !== faxId)
                .map(
                    (fax): Target => ({
                        numberId: fax.number.id,
                        displayName: null,
                        shortNumber: fax.number.shortNumber,
                        type: TargetType.FAX,
                    }),
                );

            return ivrTargets.concat(
                targetsData.data.groups.items.map(
                    (group): Target => ({
                        numberId: group.number.id,
                        displayName: null,
                        shortNumber: group.number.shortNumber,
                        type: TargetType.GROUP,
                    }),
                ),
                targetsData.data.users.items.map(
                    (ext): Target => ({
                        numberId: ext.number.id,
                        displayName: ext.displayName,
                        shortNumber: ext.number.shortNumber,
                        type: TargetType.EXTENSION,
                    }),
                ),
            );
        }
    }

    return (
        <div style={{marginBottom: 20}}>
            <FormattedPageTitle desc={defineMessage({id: "delete", defaultMessage: "Delete"})} />
            <Section
                label={defineMessage({
                    id: "fax_inconsistency.please_solve",
                    defaultMessage: "Please solve the following inconsistencies",
                })}
                hidden={!data.relatedData.overrideCaller.length && !data.relatedData.ivrActions.length}
            >
                <FormContainer>
                    {data.relatedData.overrideCaller.map((extension, index) => (
                        <React.Fragment key={index}>
                            <Typography>
                                {exclamationIcon()}
                                <FormattedMessage
                                    id="fax_inconsistency.select_extension"
                                    defaultMessage="Select new alternate caller number for the user {name}"
                                    values={{
                                        name: (
                                            <Link to={`/domains/${domainId}/users/${extension.id}/generic`}>
                                                {extension.displayName}
                                            </Link>
                                        ),
                                    }}
                                />
                            </Typography>
                            <TargetSelector
                                handleChange={(id) => {
                                    form.handleChange(id, extension.id, ReferenceType.USER);
                                }}
                                variant="standard"
                                selected={form.selections[extension.id]}
                                targets={getTarget(ReferenceType.USER)}
                                style={TargetSelectorStyle}
                                notSelectedText={defineMessage({id: "clear", defaultMessage: "Clear"})}
                            />
                        </React.Fragment>
                    ))}
                </FormContainer>
                <FormContainer>
                    {data.relatedData.ivrActions.map((ivr, index) => (
                        <React.Fragment key={index}>
                            <Typography>
                                {exclamationIcon()}
                                <FormattedMessage
                                    id="fax_inconsistency.select_ivr"
                                    defaultMessage="Button {action} in the menu of attendant {ivr} refers to this fax"
                                    values={{
                                        action: ivr.button,
                                        ivr: (
                                            <Link to={`/domains/${domainId}/ivrs/${ivr.ivrProfile.ivr.id}`}>
                                                {ivr.ivrProfile.ivr.name}
                                            </Link>
                                        ),
                                    }}
                                />
                            </Typography>
                            <TargetSelector
                                handleChange={(id) => {
                                    form.handleChange(id, ivr.id, ReferenceType.IVR);
                                }}
                                variant="standard"
                                selected={form.selections[ivr.id]}
                                targets={getTarget(ReferenceType.IVR)}
                                style={TargetSelectorStyle}
                                notSelectedText={defineMessage({id: "clear", defaultMessage: "Clear"})}
                            />
                        </React.Fragment>
                    ))}
                </FormContainer>
            </Section>
            <FormattedFormRow>
                <div style={{margin: "1em 0"}}>
                    <FormActionButtons
                        mainButtonText={msg_delete}
                        mainAction={() => {
                            form.delete();
                        }}
                        mainButtonColor="secondary"
                        secondaryAction={() => {
                            form.reset();
                        }}
                        secondaryButtonHidden={
                            !data.relatedData.overrideCaller.length && !data.relatedData.ivrActions.length
                        }
                    />
                </div>
            </FormattedFormRow>
        </div>
    );
};

export default DeleteFaxPage;

const TargetSelectorStyle = {
    marginLeft: "25px",
    marginRight: "25px",
    width: "50%",
} as React.CSSProperties;
